import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { FaAngleRight, FaPlay } from "react-icons/fa6";
import { useAppDispatch } from "../../../utils/hooks";
import {
  setViewImage,
  updateViewState,
} from "../../../store/Slices/ViewManagerSlice";
import { useConversationMedia } from "../../../store/api/useConversationMedia";
import { ClipLoader } from "react-spinners";
import { useTheme } from "../../../context/ThemeProvider";
import { useConversationDocuments } from "../../../store/api/useConversationDocuments";
import { useConversationLinks } from "../../../store/api/useConversationLinks";
import TextTranslate from "../../../utils/TextTranslate";
import {
  getMessagePlayUrl,
  type MediaPlayFields,
} from "../../../utils/mediaPlayUrl";

function mediaPlayUrl(message: MediaPlayFields) {
  if (message.message_type === "video") {
    return getMessagePlayUrl(message);
  }
  return String(message.message_content || "").replace(/\\/g, "/");
}

function openMediaGallery(
  dispatch: ReturnType<typeof useAppDispatch>,
  records: MediaPlayFields[],
  index: number,
) {
  const urls = records
    .map((m) => mediaPlayUrl(m))
    .filter((url): url is string => !!url);
  if (!urls.length) return;
  dispatch(
    setViewImage({
      show_image: true,
      image_src: urls,
      currentIndex: Math.min(Math.max(index, 0), urls.length - 1),
    }),
  );
}

export default function LinksAndMedia() {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useConversationMedia();
  const { data: documents } = useConversationDocuments();
  const { data: links } = useConversationLinks();
  // @ts-ignore
  const { theme } = useTheme();
  const records = data?.data?.Records ?? [];
  const mediaCount = data?.data?.pagenation?.total_records || 0;
  const total =
    mediaCount +
    (documents?.data?.pagenation?.total_records || 0) +
    (links?.data?.pagenation?.total_records || 0);

  return (
    <div className="overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      <div
        onClick={() => {
          dispatch(updateViewState({ showMediaDocLinks: true }));
        }}
        className="flex cursor-pointer items-center justify-between px-4 py-3.5 text-[15px] text-darkText active:bg-black/[0.04] dark:active:bg-white/[0.06]"
      >
        <div className="flex min-w-0 items-center gap-3">
          <img
            className="h-5 w-5 shrink-0 object-contain"
            src={`${
              theme == "dark"
                ? "/DarkIcons"
                : theme == "light"
                  ? "/LightIcons"
                  : "/DarkIcons"
            }/meida_icon.png`}
            alt=""
          />
          <span className="truncate">
            <TextTranslate text="Media, links and docs" />
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1 text-[15px] text-lightText">
          <span className="min-w-[1.25rem] text-right tabular-nums">
            {total}
          </span>
          <FaAngleRight className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </div>
      </div>

      {(isLoading || mediaCount > 0) && (
        <div className="grid grid-cols-3 gap-2 border-t border-black/[0.06] px-3 pb-3 pt-2 dark:border-white/[0.08]">
          {isLoading ? (
            <div className="col-span-3 grid h-24 place-content-center">
              <ClipLoader
                color={theme == "dark" ? "#FCC604" : "#000000"}
                size={20}
              />
            </div>
          ) : (
            records.slice(0, 3).map((message, index) => {
              if (message.message_type == "image") {
                return (
                  <div
                    key={message.message_id}
                    className="h-24 w-full cursor-pointer overflow-hidden rounded-xl"
                    onClick={() =>
                      openMediaGallery(dispatch, records, index)
                    }
                  >
                    <LoadingSkeletonImageDynamic
                      radius=".75rem"
                      className="h-24 w-full cursor-pointer object-cover"
                      image_height="100%"
                      image_url={message.message_content}
                      image_width=""
                    />
                  </div>
                );
              }

              if (message.message_type == "gif") {
                return (
                  <img
                    key={message.message_id}
                    onClick={() =>
                      openMediaGallery(dispatch, records, index)
                    }
                    src={message.message_content}
                    className="h-24 w-full cursor-pointer rounded-xl object-cover"
                    alt=""
                  />
                );
              }

              return (
                <div
                  key={message.message_id}
                  onClick={() => openMediaGallery(dispatch, records, index)}
                  className="relative cursor-pointer overflow-hidden rounded-xl"
                >
                  <div className="absolute grid h-full w-full place-content-center">
                    <FaPlay className="h-11 w-11 rounded-full bg-[#0000008F] p-3 text-white" />
                  </div>
                  <img
                    className="h-24 w-full cursor-pointer rounded-xl object-cover"
                    src={
                      message.message_type == "video"
                        ? message.message_thumbnail
                        : message.message_content
                    }
                    alt=""
                  />
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
